import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { BaseService } from '../../services/base.service';

export interface DroneSurveyBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/** One published drone tile pyramid, as returned by analysis-management. */
export interface DroneSurvey {
  /** UUID. The service's internal integer key is never exposed. */
  id: string;
  tenantId: number;
  name: string;
  /** Full tile URL with {z}/{x}/{y} placeholders. */
  tileUrlTemplate: string;
  minZoom: number;
  maxZoom: number;
  bounds: DroneSurveyBounds;
  /** ISO date, or null when the capture date is unknown. */
  capturedAt: string | null;
}

// Root-provided: consumed by both the maps module and the older mapping
// screen, which lives in AppModule and cannot see MapsModule's providers.
@Injectable({ providedIn: 'root' })
export class DroneSurveyService extends BaseService {
  private readonly API_DRONE_SURVEY_URL = this.API_BASE_URL + 'analysis-management/droneSurveys';

  readonly surveys = signal<DroneSurvey[]>([]);
  readonly loading = signal(false);
  /** True once a load has completed, successfully or not. */
  readonly loaded = signal(false);

  constructor(private http: HttpClient) {
    super();
  }

  /**
   * Load the tenant's surveys.
   *
   * Failures are swallowed to an empty list rather than left to the global
   * interceptor. This is imagery enrichment, not a feature the user asked for,
   * so a tenant with no surveys configured, or an endpoint not yet deployed to
   * a given environment, must not raise a snackbar on every map load.
   */
  loadSurveys(tenantId: number | null): Observable<DroneSurvey[]> {
    if (!tenantId) {
      this.surveys.set([]);
      this.loaded.set(true);
      return of([]);
    }
    this.loading.set(true);
    return this.http
      .get<DroneSurvey[]>(`${this.API_DRONE_SURVEY_URL}?tenantId=${tenantId}`)
      .pipe(
        catchError(err => {
          console.warn('[DroneSurveyService] could not load drone surveys', err?.status ?? err);
          return of([] as DroneSurvey[]);
        }),
        tap(surveys => {
          this.surveys.set(Array.isArray(surveys) ? surveys : []);
          this.loading.set(false);
          this.loaded.set(true);
        })
      );
  }
}
